import React, { useEffect, useRef, useState } from "react";
import {
  AgentEventsEnum,
  LiveAvatarSession,
  SessionEvent,
} from "@heygen/liveavatar-web-sdk";
import {
  LIVEAVATAR_IDLE_CLOSE_MS,
  LIVEAVATAR_IDLE_WARN_MS,
} from "../../Config";
import CoachingService from "../../services/CoachingService";

const KEEP_ALIVE_MS = 60_000;
const SPEAK_TIMEOUT_MS = 60_000;
const IDLE_TICK_MS = 1_000;

const isBenignSessionCommandError = (message) => {
  const text = String(message || "").toLowerCase();
  return (
    text.includes("needs to be connected") ||
    text.includes("send command event") ||
    text.includes("session is not connected")
  );
};

/**
 * LiveAvatar video coach: mint once per coaching session, speak via repeat(text).
 * Idle pause stops the LiveAvatar room (billing) while the coaching WS stays open.
 * Token stays in memory only.
 */
const LiveAvatarCoach = ({
  coachingSessionId,
  textToSpeak,
  onSpeakingComplete,
  onReadyChange,
  onError,
  onPaused,
  onIdleWarning,
  active,
  isBusy = false,
  allowRecovery = true,
  resumeRequestId = 0,
  statusLabel = "Listening…",
}) => {
  const videoRef = useRef(null);
  const sessionRef = useRef(null);
  const streamReadyRef = useRef(false);
  const speakingRef = useRef(false);
  const pendingTextRef = useRef(null);
  const speakTimeoutRef = useRef(null);
  const keepAliveRef = useRef(null);
  const idleTimerRef = useRef(null);
  const recoveredRef = useRef(false);
  const mountedRef = useRef(true);
  const intentionalPauseRef = useRef(false);
  const pausedRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const idleWarnedRef = useRef(false);
  const isBusyRef = useRef(isBusy);
  const allowRecoveryRef = useRef(allowRecovery);
  const prevBusyRef = useRef(isBusy);
  const lastResumeRequestRef = useRef(resumeRequestId);
  const [connecting, setConnecting] = useState(false);
  const [ready, setReady] = useState(false);
  const [paused, setPaused] = useState(false);
  const [idleWarning, setIdleWarning] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  isBusyRef.current = isBusy;
  allowRecoveryRef.current = allowRecovery;
  pausedRef.current = paused;

  const reportError = (message) => {
    // Expected after idle stop / disconnect — do not surface to the UI.
    if (isBenignSessionCommandError(message)) {
      return;
    }
    setAvatarError(true);
    if (typeof onError === "function") {
      onError(message);
    }
  };

  const touchActivity = () => {
    lastActivityRef.current = Date.now();
    idleWarnedRef.current = false;
    setIdleWarning(false);
  };

  const statusDotClass = avatarError
    ? "coach-status-dot coach-status-dot-error"
    : paused
      ? "coach-status-dot coach-status-dot-idle"
      : ready
        ? "coach-status-dot coach-status-dot-ready"
        : "coach-status-dot coach-status-dot-idle";

  const clearSpeakTimeout = () => {
    if (speakTimeoutRef.current) {
      clearTimeout(speakTimeoutRef.current);
      speakTimeoutRef.current = null;
    }
  };

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearInterval(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const finishSpeaking = () => {
    clearSpeakTimeout();
    speakingRef.current = false;
    touchActivity();
    if (typeof onSpeakingComplete === "function") {
      onSpeakingComplete();
    }
  };

  const speakText = (text) => {
    const session = sessionRef.current;
    if (!session || !streamReadyRef.current || !text || pausedRef.current) {
      return;
    }
    speakingRef.current = true;
    touchActivity();
    clearSpeakTimeout();
    speakTimeoutRef.current = setTimeout(() => {
      finishSpeaking();
    }, SPEAK_TIMEOUT_MS);
    try {
      const result = session.repeat(text);
      if (result && typeof result.catch === "function") {
        result.catch((e) => {
          finishSpeaking();
          reportError(e?.message || "Avatar could not speak.");
        });
      }
    } catch (e) {
      finishSpeaking();
      reportError(e?.message || "Avatar could not speak.");
    }
  };

  const stopSession = async ({ intentionalPause = false } = {}) => {
    clearSpeakTimeout();
    clearIdleTimer();
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
    // Mark pause before stop so late SDK command errors are ignored.
    if (intentionalPause) {
      intentionalPauseRef.current = true;
      pausedRef.current = true;
      setPaused(true);
      setConnecting(false);
    }
    // Drop local speak lock without calling onSpeakingComplete — parent releases /
    // re-queues via onReadyChange(false) so reconnect can resume the turn.
    streamReadyRef.current = false;
    speakingRef.current = false;
    pendingTextRef.current = null;
    setReady(false);
    setIdleWarning(false);
    idleWarnedRef.current = false;
    if (typeof onReadyChange === "function") {
      onReadyChange(false);
    }
    const session = sessionRef.current;
    sessionRef.current = null;
    if (session) {
      try {
        await session.stop();
      } catch {
        // ignore stop errors on teardown
      }
    }
    if (intentionalPause && typeof onPaused === "function") {
      onPaused();
    }
  };

  const pauseForIdle = async () => {
    if (intentionalPauseRef.current || pausedRef.current || !sessionRef.current) {
      return;
    }
    clearIdleTimer();
    await stopSession({ intentionalPause: true });
  };

  const startIdleTimer = () => {
    clearIdleTimer();
    touchActivity();
    idleTimerRef.current = setInterval(() => {
      if (
        !mountedRef.current ||
        pausedRef.current ||
        intentionalPauseRef.current ||
        !streamReadyRef.current ||
        speakingRef.current ||
        isBusyRef.current
      ) {
        return;
      }
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= LIVEAVATAR_IDLE_CLOSE_MS) {
        pauseForIdle();
        return;
      }
      if (idleMs >= LIVEAVATAR_IDLE_WARN_MS && !idleWarnedRef.current) {
        idleWarnedRef.current = true;
        setIdleWarning(true);
        if (typeof onIdleWarning === "function") {
          onIdleWarning();
        }
      }
    }, IDLE_TICK_MS);
  };

  const startSession = async () => {
    if (!coachingSessionId || !active || pausedRef.current) {
      return;
    }
    intentionalPauseRef.current = false;
    setPaused(false);
    pausedRef.current = false;
    setConnecting(true);
    setAvatarError(false);
    setIdleWarning(false);
    try {
      const response = await CoachingService().createLiveAvatarToken(
        coachingSessionId
      );
      if (!mountedRef.current) {
        return;
      }
      if (response?.status !== 200 || !response?.data?.sessionToken) {
        throw new Error(response?.message || "Could not start avatar session.");
      }

      const session = new LiveAvatarSession(response.data.sessionToken, {
        voiceChat: false,
      });
      sessionRef.current = session;

      session.on(SessionEvent.SESSION_STREAM_READY, () => {
        if (!mountedRef.current || intentionalPauseRef.current) {
          return;
        }
        streamReadyRef.current = true;
        setReady(true);
        setAvatarError(false);
        setConnecting(false);
        touchActivity();
        startIdleTimer();
        if (videoRef.current) {
          try {
            session.attach(videoRef.current);
          } catch {
            // attach may fail if element unmounted
          }
        }
        if (typeof onReadyChange === "function") {
          onReadyChange(true);
        }
        if (pendingTextRef.current) {
          const next = pendingTextRef.current;
          pendingTextRef.current = null;
          speakText(next);
        }
      });

      session.on(SessionEvent.SESSION_DISCONNECTED, () => {
        streamReadyRef.current = false;
        setReady(false);
        clearIdleTimer();
        if (typeof onReadyChange === "function") {
          onReadyChange(false);
        }
        if (intentionalPauseRef.current || pausedRef.current) {
          return;
        }
        // Session ending / wind-down: no remint (mint fails after COMPLETED) and
        // no Continue bar — parent reveal fallback handles stuck speech.
        if (!allowRecoveryRef.current) {
          setConnecting(false);
          return;
        }
        if (!recoveredRef.current && active && mountedRef.current) {
          recoveredRef.current = true;
          stopSession().then(() => startSession());
        } else if (mountedRef.current && active) {
          // Unexpected drop after recovery exhausted — treat as paused for Continue.
          intentionalPauseRef.current = true;
          setPaused(true);
          pausedRef.current = true;
          setConnecting(false);
          if (typeof onPaused === "function") {
            onPaused();
          }
        }
      });

      session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
        finishSpeaking();
      });

      await session.start();

      keepAliveRef.current = setInterval(() => {
        sessionRef.current?.keepAlive?.().catch(() => {});
      }, KEEP_ALIVE_MS);
    } catch (e) {
      if (!mountedRef.current) {
        return;
      }
      setConnecting(false);
      setReady(false);
      reportError(e?.message || "Could not start avatar session.");
      intentionalPauseRef.current = true;
      setPaused(true);
      pausedRef.current = true;
      if (typeof onPaused === "function") {
        onPaused();
      }
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    const onPageHide = () => {
      intentionalPauseRef.current = true;
      const session = sessionRef.current;
      sessionRef.current = null;
      if (session) {
        try {
          session.stop();
        } catch {
          // ignore
        }
      }
      if (coachingSessionId) {
        CoachingService().stopLiveAvatarSessionKeepalive(coachingSessionId);
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      stopSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachingSessionId]);

  useEffect(() => {
    if (active && coachingSessionId && !sessionRef.current && !pausedRef.current) {
      recoveredRef.current = false;
      intentionalPauseRef.current = false;
      startSession();
    }
    if (!active && sessionRef.current) {
      intentionalPauseRef.current = false;
      setPaused(false);
      pausedRef.current = false;
      stopSession();
    }
    if (!active) {
      setPaused(false);
      pausedRef.current = false;
      intentionalPauseRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, coachingSessionId]);

  useEffect(() => {
    if (resumeRequestId === lastResumeRequestRef.current) {
      return;
    }
    lastResumeRequestRef.current = resumeRequestId;
    if (!resumeRequestId || !active || !coachingSessionId) {
      return;
    }
    intentionalPauseRef.current = false;
    setPaused(false);
    pausedRef.current = false;
    recoveredRef.current = false;
    setAvatarError(false);
    if (sessionRef.current) {
      stopSession().then(() => startSession());
    } else {
      startSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeRequestId]);

  useEffect(() => {
    const wasBusy = prevBusyRef.current;
    prevBusyRef.current = isBusy;
    if (wasBusy && !isBusy) {
      touchActivity();
    }
    if (isBusy) {
      setIdleWarning(false);
      idleWarnedRef.current = false;
    }
  }, [isBusy]);

  useEffect(() => {
    if (!textToSpeak) {
      return;
    }
    touchActivity();
    if (pausedRef.current || intentionalPauseRef.current) {
      pendingTextRef.current = textToSpeak;
      return;
    }
    if (!streamReadyRef.current || !sessionRef.current) {
      pendingTextRef.current = textToSpeak;
      return;
    }
    speakText(textToSpeak);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textToSpeak]);

  const overlayText = (() => {
    if (paused) {
      return "Coach disconnected to save connection";
    }
    if (connecting) {
      return "Connecting coach avatar…";
    }
    if (!ready) {
      return "Preparing avatar…";
    }
    return null;
  })();

  return (
    <div className={`liveavatar-coach${paused ? " liveavatar-coach--paused" : ""}`}>
      <div className="coach-video-tile">
        <video
          ref={videoRef}
          className="coach-video-tile-media"
          autoPlay
          playsInline
          muted={false}
        />
        {overlayText && (
          <div className="liveavatar-coach-overlay" aria-live="polite">
            {overlayText}
          </div>
        )}
        {idleWarning && !paused && (
          <div className="liveavatar-idle-warning" aria-live="polite">
            Coach will disconnect soon due to inactivity
          </div>
        )}
        <div className="coach-status-badge" aria-live="polite">
          {paused ? "Paused" : statusLabel}
        </div>
        <div className="coach-name-badge">
          <span className={statusDotClass} aria-hidden="true" />
          <span>AI Coach</span>
        </div>
      </div>
    </div>
  );
};

export default LiveAvatarCoach;
